import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, ShieldCheck, Radio, Cpu, Boxes } from "lucide-react";
import { NetworkCanvas } from "@/components/NetworkCanvas";
import { Section, SectionHeading, Reveal } from "@/components/primitives";
import { StrategiesGrid } from "@/components/StrategyCard";
import { ArchitectureDiagram, RiskSection, TrustPrinciple } from "@/components/marketing";
import { QCoreEngine, InsideQCore, LiveTerminal, VerifyOnChainTeaser } from "@/components/qcore";
import { getSettings, trackEvent } from "@/lib/api";

const HERO_STATS = [
  ["Opportunities Scanned", "4.8M+", "24h rolling"],
  ["Markets Monitored", "Hundreds", "CEX · DEX · Futures"],
  ["Strategies Running", "6", "in parallel"],
];

function Hero() {
  return (
    <section className="relative min-h-screen flex items-center pt-28 pb-16 overflow-hidden qs-aurora" data-testid="hero">
      <div className="absolute inset-0 bg-[#030305]" />
      <NetworkCanvas className="absolute inset-0 h-full w-full opacity-70" density={80} />
      <div className="absolute inset-0 qs-grid opacity-[0.10] pointer-events-none" aria-hidden="true" />

      <div className="relative mx-auto max-w-7xl w-full px-5 sm:px-8 lg:px-12">
        <div className="grid lg:grid-cols-12 gap-12 lg:gap-8 items-center">
          <div className="lg:col-span-6">
            <motion.div
              initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}
              className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/[0.03] px-4 py-1.5 mb-8"
            >
              <span className="qs-pulse-dot h-1.5 w-1.5 rounded-full bg-[#00E676] text-[#00E676]" />
              <span className="font-mono text-xs text-slate-400">Q-Core Engine · Operational</span>
            </motion.div>

            <motion.h1
              initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.05 }}
              className="text-4xl sm:text-5xl lg:text-[64px] font-black text-white leading-[1.02] tracking-tight"
            >
              The Intelligence Layer for{" "}
              <span className="text-[#2DD4BF] qs-glow-text">Automated Arbitrage.</span>
            </motion.h1>

            <motion.p
              initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.15 }}
              className="mt-6 text-base md:text-lg text-slate-400 max-w-xl leading-relaxed"
            >
              One engine. Multiple markets. Continuous opportunity. Q-Core analyzes fragmented digital-asset
              markets across centralized exchanges, decentralized protocols, and blockchain networks — as software infrastructure.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.25 }}
              className="mt-9 flex flex-wrap gap-3"
            >
              <Link
                to="/q-core" data-testid="hero-explore-qcore"
                onClick={() => trackEvent("cta_click", { cta: "explore_qcore" })}
                className="group inline-flex items-center gap-2 px-6 py-3.5 rounded-full bg-[#0E9E90] text-white font-medium shadow-[0_0_24px_rgba(14,158,144,0.35)] hover:bg-[#1E4BCC] transition-colors"
              >
                Explore Q-Core
                <ArrowRight size={18} strokeWidth={2} className="group-hover:translate-x-1 transition-transform" />
              </Link>
              <Link
                to="/technology" data-testid="hero-view-technology"
                className="inline-flex items-center gap-2 px-6 py-3.5 rounded-full border border-white/15 bg-white/5 text-white hover:bg-white/10 transition-colors"
              >
                View Technology
              </Link>
            </motion.div>

            <motion.div
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.7, delay: 0.4 }}
              className="mt-10 flex flex-wrap items-center gap-x-6 gap-y-2 text-xs text-slate-500"
            >
              <span className="inline-flex items-center gap-1.5"><ShieldCheck size={15} strokeWidth={1.5} className="text-[#2DD4BF]" /> Non-custodial architecture</span>
              <span className="inline-flex items-center gap-1.5"><Radio size={15} strokeWidth={1.5} className="text-[#2DD4BF]" /> On-chain verifiable activity</span>
              <span className="inline-flex items-center gap-1.5"><Boxes size={15} strokeWidth={1.5} className="text-[#2DD4BF]" /> Six parallel strategies</span>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.5 }}
              className="mt-10 grid grid-cols-3 gap-3 max-w-xl"
            >
              {HERO_STATS.map(([l, v, s]) => (
                <div key={l} className="rounded-xl border border-white/5 bg-[#0A0B10]/70 backdrop-blur-xl p-4">
                  <p className="font-mono text-xl md:text-2xl text-white">{v}</p>
                  <p className="text-[11px] text-slate-300 mt-1 leading-tight">{l}</p>
                  <p className="font-mono text-[9px] uppercase tracking-widest text-slate-600 mt-1">{s}</p>
                </div>
              ))}
            </motion.div>
          </div>

          <motion.div
            initial={{ opacity: 0, scale: 0.92 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.9, delay: 0.2 }}
            className="lg:col-span-6"
          >
            <QCoreEngine />
          </motion.div>
        </div>
      </div>
    </section>
  );
}

function MarketCoverage() {
  const [settings, setSettings] = useState(null);
  useEffect(() => { getSettings().then(setSettings).catch(() => {}); }, []);
  const headline = settings?.market_coverage?.headline || "Hundreds of Markets and Growing";
  return (
    <Section grid>
      <div className="grid lg:grid-cols-2 gap-12 items-center">
        <Reveal>
          <SectionHeading
            eyebrow="Market Coverage"
            title="Fragmented markets, unified analysis."
            sub="Q-Core is designed to monitor fragmented cryptocurrency markets across centralized exchanges, decentralized protocols, and blockchain networks — analyzing pricing relationships wherever they emerge."
          />
        </Reveal>
        <Reveal delay={0.1}>
          <div className="grid grid-cols-2 gap-4" data-testid="market-coverage">
            <div className="col-span-2 rounded-xl border border-[#0E9E90]/25 bg-[#0E9E90]/[0.05] p-6">
              <p className="font-mono text-xs uppercase tracking-widest text-[#2DD4BF]">Coverage</p>
              <p className="text-2xl md:text-3xl font-black text-white mt-2">{headline}</p>
              <p className="text-sm text-slate-400 mt-2">Displayed conservatively. Figures are shown only where independently verifiable.</p>
            </div>
            {[
              ["Centralized Exchanges", "CEX"],
              ["Decentralized Protocols", "DEX"],
              ["Layer-2 Networks", "L2"],
              ["Multi-Chain", "EVM + Tron"],
            ].map(([l, tag]) => (
              <div key={l} className="rounded-xl border border-white/5 bg-[#0A0B10] p-5">
                <p className="font-mono text-[11px] text-[#0E9E90] uppercase tracking-widest">{tag}</p>
                <p className="text-sm text-white mt-2 font-semibold">{l}</p>
              </div>
            ))}
          </div>
        </Reveal>
      </div>
    </Section>
  );
}

export default function Home() {
  useEffect(() => { trackEvent("page_view", { page: "home" }); }, []);

  return (
    <div>
      <Hero />

      <Section grid>
        <SectionHeading
          align="center" eyebrow="Inside Q-Core"
          title="A system, operating continuously."
          sub="From detection to monitoring, Q-Core follows a deterministic pipeline across every market it observes."
          className="mb-14"
        />
        <InsideQCore />
      </Section>

      <Section id="tech-story" grid>
        <SectionHeading
          align="center" eyebrow="Technology Story"
          title="One Engine. Multiple Arbitrage Strategies."
          sub="Q-Core is designed to analyze multiple types of arbitrage opportunities simultaneously across the digital asset ecosystem."
          className="mb-14"
        />
        <StrategiesGrid />
        <div className="text-center mt-10">
          <Link to="/strategies" className="inline-flex items-center gap-2 text-sm text-[#2DD4BF] hover:text-white transition-colors">
            Explore all six strategies <ArrowRight size={15} strokeWidth={2} />
          </Link>
        </div>
      </Section>

      <Section grid>
        <SectionHeading
          eyebrow="Live Opportunity Feed"
          title="Real spreads. Live from the market."
          sub="Genuine cross-exchange price differences pulled in real time from public exchange APIs (OKX · Kraken · Gate). Prices are real and read-only — execution is simulated; Q-Core does not place live trades in this demo."
          className="mb-10"
        />
        <LiveTerminal />
      </Section>

      <MarketCoverage />

      <Section grid>
        <SectionHeading
          align="center" eyebrow="Non-Custodial Architecture"
          title="Your Assets. Your Control."
          sub="Quantum Scalp is designed around a non-custodial architecture. Where trading infrastructure requires assets to be transferred to supported venues for execution, we explain that process transparently."
          className="mb-14"
        />
        <ArchitectureDiagram />
      </Section>

      <Section>
        <VerifyOnChainTeaser />
      </Section>

      <Section>
        <RiskSection />
      </Section>

      <Section>
        <TrustPrinciple />
      </Section>

      <Section grid>
        <div className="relative overflow-hidden rounded-2xl border border-white/10 bg-gradient-to-b from-[#12131A] to-[#0A0B10] p-8 md:p-14 text-center qs-aurora">
          <div className="relative">
            <h2 className="text-3xl md:text-4xl font-black text-white">Ready to explore the technology?</h2>
            <p className="mt-4 text-slate-400 max-w-xl mx-auto">
              Understand Q-Core, review the architecture, and see what a license unlocks.
            </p>
            <div className="mt-8 flex flex-wrap justify-center gap-3">
              <Link to="/pricing" className="px-6 py-3.5 rounded-full bg-[#0E9E90] text-white font-medium shadow-[0_0_24px_rgba(14,158,144,0.35)] hover:bg-[#1E4BCC] transition-colors" data-testid="home-cta-pricing">
                View License Options
              </Link>
              <Link to="/q-core" className="px-6 py-3.5 rounded-full border border-white/15 bg-white/5 text-white hover:bg-white/10 transition-colors">
                Inside Q-Core
              </Link>
            </div>
          </div>
        </div>
      </Section>
    </div>
  );
}
